import React, { useRef, useEffect } from 'react';
import { Input } from 'antd';
import { PhoneOutlined } from '@ant-design/icons';
import { digitsOnly, formatPhoneInputDisplay, getTenDigitsForInput } from '../utils/phoneUtils';

export interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  size?: 'large' | 'middle' | 'small';
  disabled?: boolean;
  prefix?: React.ReactNode;
  id?: string;
}

const DEFAULT_PLACEHOLDER = 'Введите номер телефона';

/**
 * Поле ввода телефона: при вводе первой цифры автоматически добавляется +7, далее форматирование +7 (XXX) XXX-XX-XX.
 * В форму отдаётся нормализованное значение: +7XXXXXXXXXX.
 */
export const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  placeholder = DEFAULT_PLACEHOLDER,
  size = 'large',
  disabled = false,
  prefix,
  id,
}) => {
  const inputRef = useRef<any>(null);
  const tenDigits = value ? getTenDigitsForInput(value) : '';
  // Отображение: если есть цифры, показываем "+7" + форматированные цифры
  const displayValue = tenDigits ? `+7${formatPhoneInputDisplay(tenDigits)}` : '';

  // Курсор в конец строки после обновления значения
  useEffect(() => {
    const el = inputRef.current?.input ?? inputRef.current;
    if (!el || typeof el.setSelectionRange !== 'function') return;
    const len = displayValue.length;
    requestAnimationFrame(() => {
      el.setSelectionRange(len, len);
    });
  }, [displayValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Убираем "+7" из начала, если пользователь его ввёл или он был в отображении
    const cleaned = raw.replace(/^\+7\s*/, '');
    const digits = digitsOnly(cleaned);
    
    // Если пользователь начал вводить (есть хотя бы одна цифра), добавляем +7
    // Если удалил всё, оставляем пустым
    if (digits.length === 0) {
      onChange?.('');
      return;
    }
    
    const ten = digits.slice(0, 10);
    const normalized = `+7${ten}`;
    onChange?.(normalized);
  };

  return (
    <Input
      ref={inputRef}
      id={id}
      prefix={prefix ?? <PhoneOutlined />}
      placeholder={placeholder}
      value={displayValue}
      onChange={handleChange}
      size={size}
      disabled={disabled}
      maxLength={20}
      inputMode="tel"
      autoComplete="tel"
    />
  );
};
